import React, { useState, useEffect } from 'react';
import { TriagePatientValue, TriagedPatient } from './types';
import {
  createTriageData,
  getTriageEntry,
  ShouldUpdateTriage,
  useTriagePatientData
} from './utils';
import { useTriage } from '.';
import { useUrlState } from '../active-dialogs/url';

const TriagePatientContext = React.createContext<TriagePatientValue>({
  updatePatientId: null,
  patientData: null
});

export const TriagePatientProvider: React.FC = (props) => {
  const [patientId, updatePatientId] = useState<number>(null);
  const [patientDataFallback, setPatientDataFallback] =
    useState<TriagedPatient>(null);
  const { isViewingPatientFromUrl } = useUrlState();
  const {
    data: triageData,
    isLoading,
    addTriagedPatient,
    setTriageData
  } = useTriage();
  const { data: patientData, isLoading: isPatientRequestLoading } =
    useTriagePatientData(patientId, true);

  // Reset fallback when switching patients
  useEffect(() => {
    setPatientDataFallback(null);
  }, [patientId]);

  // Handle the single triage entry insertion
  useEffect(() => {
    // When the triage data is not set, create an empty shell
    if (!triageData && !isLoading) {
      setTriageData(createTriageData());
    }
    if (!triageData && isViewingPatientFromUrl) {
      setTriageData(createTriageData());
    }
    // Add or update the triage entry to the existing set
    if (patientData && triageData) {
      const { patient: entry, contacts } = patientData;
      setPatientDataFallback(entry);
      const existingEntry = triageData.triages[entry.patient.id];
      let existingTriagePatient = null;
      if (existingEntry) {
        existingTriagePatient = getTriageEntry(
          triageData,
          existingEntry.patientId
        );
        const contextNote = existingTriagePatient?.patient?.note;
        const triageNote = entry.patient.note;

        if (contextNote && triageNote && contextNote !== triageNote) {
          return;
        }
      }
      if (
        !existingEntry ||
        ShouldUpdateTriage(existingEntry, entry) ||
        (existingTriagePatient?.patient?.tasks === undefined &&
          entry?.patient?.tasks !== undefined)
      ) {
        addTriagedPatient(entry, contacts);
      }
    }
  }, [patientData, triageData]);

  useEffect(() => {
    if (
      !isViewingPatientFromUrl &&
      triageData &&
      triageData.totalPatients === 0
    ) {
      setTriageData(null);
    }
  }, [isViewingPatientFromUrl]);

  const value: TriagePatientValue = {
    updatePatientId,
    // Need to reference the actual source of data in triage data
    patientData: getTriageEntry(triageData, patientId) || patientDataFallback,
    // Only consider the patient as "loading" once we have a valid patient id to fetch.
    isPatientLoading:
      patientId != null && patientId > 0 ? isPatientRequestLoading : false
  };

  return <TriagePatientContext.Provider value={value} {...props} />;
};

export function useTriagePatient(): TriagePatientValue {
  const context = React.useContext(TriagePatientContext);
  if (context === undefined) {
    throw new Error(
      'useTriagePatient must be used within a TriagePatientProvider'
    );
  }
  return context;
}
